const LocalRoutes = {
    HOME: "/",
    LOGIN: "/login",
    REGISTRATION: "/registration",
    PASSWORD_RESET: "/password-reset",

    DASHBOARD: "/dashboard",
    BILLING: "/billing",
    METERING_POINTS: "/metering-points",
    USER_DATA: "/user-data",
    LEGAL_PERSONS: "/legal-persons",
    ENERGY_DATA: "/energy-data",
    ENERGY_DATA_INFO_FOR_CONTRACT: "/energy-data/:meteringPointContractId/info",
    DATA_EXPORTS: "/data-exports",
    INVOICES: "/invoices",
    NOTIFICATIONS: "/notifications",
    MEMBER_CONTRACTS: "/member-contracts",
    CONTRACT_DOCUMENTS: "/contract-documents",
    METERING_POINT_CONTRACTS: "/metering-point-contracts",

    ADMIN: "/admin",
    ADMIN_DASHBOARD: "/admin/dashboard",
    ADMIN_ENERGY_COMMUNITIES: "/admin/energy-communities",
    ADMIN_ENERGY_COMMUNITY_BILLING: "/admin/energy-communities/:id/billing",
    ADMIN_ENERGY_COMMUNITY_MEMBER_CONTRACTS:
        "/admin/energy-community-member-contracts",
    ADMIN_MEMBER_CONTRACT_BILLING:
        "/admin/energy-community-member-contracts/:id/billing",
    ADMIN_METERING_POINTS: "/admin/metering-points",
    ADMIN_METERING_POINT_CONTRACTS: "/admin/metering-point-contracts",
    ADMIN_METERING_POINT_CONTRACT_PROCESSES:
        "/admin/metering-point-contracts/:id/processes",
    ADMIN_PAYMENT_INFOS: "/admin/payment-infos",
    ADMIN_SEPA_MANDATES: "/admin/sepa-mandates",
    ADMIN_DOCUMENTS: "/admin/documents",
    ADMIN_LEGAL_PERSONS: "/admin/legal-persons",
    ADMIN_USERS: "/admin/users",
    ADMIN_PERMISSIONS: "/admin/permissions",
    ADMIN_GRID_OPERATORS: "/admin/grid-operators",
    ADMIN_SYSTEM_BANNER: "/admin/system-banner",
    ADMIN_TARIFF_COMPONENTS: "/admin/tariff-components",
    ADMIN_TARIFF_COMPONENT_EDIT: "/admin/tariff-components/:id",
    ADMIN_TARIFF_PACKAGES: "/admin/tariff-packages",
    ADMIN_TARIFF_PACKAGE_EDIT: "/admin/tariff-packages/:id",
    ADMIN_TARIFF_ASSIGNMENTS: "/admin/tariff-assignments",
    ADMIN_TARIFF_VIEW: "/admin/tariff-view",
    ADMIN_TARIFF_VIEW_MPC: "/admin/tariff-view/:mpcId",
    ADMIN_NOTIFICATIONS: "/admin/notifications",
    ADMIN_ONBOARDING_CONTROLLING: "/admin/onboarding-controlling",
    ADMIN_INVOICES: "/admin/invoices",
    ADMIN_INVOICE_CONTROLLING: "/admin/invoice-controlling",
    ADMIN_BILLING_BATCH_LOG: "/admin/billing-batch-log",
    ADMIN_BANK_TRANSACTIONS: "/admin/bank-transactions",
    ADMIN_MESSAGE_INTERVENTIONS: "/admin/message-interventions",
    DEBUG: "/debug",
    DEBUG_EDA_PROCESSES: "/debug/eda-processes",
    DEBUG_EDA_MESSAGES: "/debug/eda-messages",

    SUPER_ROOT: "/root",

    UNAUTHORIZED: "/unauthorized",
};

const LocalRoutesNavTargets = {
    ...LocalRoutes,
    ENERGY_DATA_INFO_FOR_CONTRACT: (meteringPointContractId: number) =>
        LocalRoutes.ENERGY_DATA_INFO_FOR_CONTRACT.replace(
            ":meteringPointContractId",
            meteringPointContractId.toString(),
        ),
    ADMIN_METERING_POINT_CONTRACT_PROCESSES: (id: number) =>
        LocalRoutes.ADMIN_METERING_POINT_CONTRACT_PROCESSES.replace(
            ":id",
            id.toString(),
        ),
    ADMIN_TARIFF_COMPONENT_EDIT: (id: number) =>
        LocalRoutes.ADMIN_TARIFF_COMPONENT_EDIT.replace(":id", id.toString()),
    ADMIN_TARIFF_PACKAGE_EDIT: (id: number) =>
        LocalRoutes.ADMIN_TARIFF_PACKAGE_EDIT.replace(":id", id.toString()),
    ADMIN_INVOICE_CONTROLLING: (
        egId: number,
        timeRangeParams: Record<string, string>,
    ) =>
        `${LocalRoutes.ADMIN_INVOICE_CONTROLLING}?egId=${egId}&${new URLSearchParams(timeRangeParams)}`,
    ADMIN_INVOICES: (filters: Array<{ id: string; value: string }>) =>
        `${LocalRoutes.ADMIN_INVOICES}?filters=${encodeURIComponent(JSON.stringify(filters))}`,
    ADMIN_ENERGY_COMMUNITY_MEMBER_CONTRACTS: (
        filters: Array<{ id: string; value: string }>,
    ) =>
        `${LocalRoutes.ADMIN_ENERGY_COMMUNITY_MEMBER_CONTRACTS}?filters=${encodeURIComponent(JSON.stringify(filters))}`,
    ADMIN_PAYMENT_INFOS: (filters: Array<{ id: string; value: string }>) =>
        `${LocalRoutes.ADMIN_PAYMENT_INFOS}?filters=${encodeURIComponent(JSON.stringify(filters))}`,
    ADMIN_USERS: (filters: Array<{ id: string; value: string }>) =>
        `${LocalRoutes.ADMIN_USERS}?filters=${encodeURIComponent(JSON.stringify(filters))}`,
    ADMIN_LEGAL_PERSONS: (filters: Array<{ id: string; value: string }>) =>
        `${LocalRoutes.ADMIN_LEGAL_PERSONS}?filters=${encodeURIComponent(JSON.stringify(filters))}`,
    ADMIN_ENERGY_COMMUNITIES: (filters: Array<{ id: string; value: string }>) =>
        `${LocalRoutes.ADMIN_ENERGY_COMMUNITIES}?filters=${encodeURIComponent(JSON.stringify(filters))}`,
    ADMIN_METERING_POINTS: (filters: Array<{ id: string; value: string }>) =>
        `${LocalRoutes.ADMIN_METERING_POINTS}?filters=${encodeURIComponent(JSON.stringify(filters))}`,
    ADMIN_BILLING_BATCH_LOG: (params: {
        name?: string;
        energyCommunityId?: number;
    }) => {
        const query = new URLSearchParams();
        if (params.energyCommunityId != null) {
            query.set("energyCommunityId", String(params.energyCommunityId));
        }
        if (params.name) {
            query.set("name", params.name);
        }
        const qs = query.toString();
        return qs
            ? `${LocalRoutes.ADMIN_BILLING_BATCH_LOG}?${qs}`
            : LocalRoutes.ADMIN_BILLING_BATCH_LOG;
    },
    ADMIN_TARIFF_VIEW_MPC: (
        mpcId: number,
        params?: { from: string; to: string },
    ) => {
        const base = LocalRoutes.ADMIN_TARIFF_VIEW_MPC.replace(
            ":mpcId",
            mpcId.toString(),
        );
        return params ? `${base}?from=${params.from}&to=${params.to}` : base;
    },
    ADMIN_ENERGY_COMMUNITY_BILLING: (id: number) =>
        LocalRoutes.ADMIN_ENERGY_COMMUNITY_BILLING.replace(
            ":id",
            id.toString(),
        ),
    ADMIN_MEMBER_CONTRACT_BILLING: (id: number) =>
        LocalRoutes.ADMIN_MEMBER_CONTRACT_BILLING.replace(":id", id.toString()),
};

const PUBLIC_ROUTES: string[] = [
    LocalRoutes.HOME,
    LocalRoutes.LOGIN,
    LocalRoutes.REGISTRATION,
    LocalRoutes.PASSWORD_RESET,
];

export { LocalRoutes, LocalRoutesNavTargets, PUBLIC_ROUTES };
